/**
 * NPCManager.ts - NPC placement, collision detection, and interaction
 * Manages NPC rendering and dialogue triggering
 */

import { Renderer } from './Renderer';
import { SpriteManager } from './SpriteManager';
import { CharacterSprite, Direction } from './CharacterSprite';
import { DialogueBox } from './DialogueSystem';
import { GameInput } from './types/engine';
import {
  Location,
  StoryFlag,
  getNPCDialogue,
  NPC_DIALOGUES,
} from './data/StoryData';
import {
  NPCPlacement,
  getNPCsAtLocation,
  getNPCAtPosition,
} from './data/NPCPlacements';

interface NPCInstance {
  placement: NPCPlacement;
  sprite: CharacterSprite;
  x: number; // Pixel position
  y: number; // Pixel position
}

export class NPCManager {
  private npcs: Map<string, NPCInstance> = new Map();
  private currentLocation: Location;
  private spriteManager: SpriteManager;
  private activeFlags: Set<StoryFlag> = new Set();
  private readonly TILE_SIZE = 16;

  // Dialogue system
  private dialogueBox: DialogueBox;
  private interactionCallback: ((npcId: string) => void) | null = null;

  constructor(spriteManager: SpriteManager, location: Location = Location.CORNELIA) {
    this.spriteManager = spriteManager;
    this.currentLocation = location;
    this.dialogueBox = new DialogueBox();
    this.loadNPCsForLocation(location);
  }

  // =========================================================================
  // Location Management
  // =========================================================================

  /**
   * Load NPCs for a specific location
   */
  loadNPCsForLocation(location: Location): void {
    this.currentLocation = location;
    this.npcs.clear();

    const npcPlacements = getNPCsAtLocation(location);

    npcPlacements.forEach(placement => {
      this.createNPCInstance(placement);
    });

    console.log(`[NPCManager] Loaded ${this.npcs.size} NPCs for ${location}`);
  }

  /**
   * Create an NPC instance with sprite
   */
  private createNPCInstance(placement: NPCPlacement): void {
    // Determine sprite class (fallback to generic NPC if not specified)
    const spriteClass = placement.spriteClass || 'FIGHTER';

    // Create character sprite
    const sprite = new CharacterSprite(this.spriteManager, {
      characterClass: spriteClass,
      x: placement.x * this.TILE_SIZE,
      y: placement.y * this.TILE_SIZE,
      tileSize: this.TILE_SIZE,
    });

    // Set initial facing direction
    sprite.setDirection(placement.facing as Direction);

    // Create instance
    const instance: NPCInstance = {
      placement,
      sprite,
      x: placement.x * this.TILE_SIZE + this.TILE_SIZE / 2,
      y: placement.y * this.TILE_SIZE + this.TILE_SIZE / 2,
    };

    this.npcs.set(placement.npcId, instance);
  }

  // =========================================================================
  // Story Flags
  // =========================================================================

  /**
   * Set active story flags
   */
  setStoryFlags(flags: Set<StoryFlag>): void {
    this.activeFlags = flags;
  }

  /**
   * Add a story flag
   */
  addStoryFlag(flag: StoryFlag): void {
    this.activeFlags.add(flag);
  }

  /**
   * Check if flag is set
   */
  hasFlag(flag: StoryFlag): boolean {
    return this.activeFlags.has(flag);
  }

  // =========================================================================
  // Collision & Interaction
  // =========================================================================

  /**
   * Check if an NPC is at the given tile position
   */
  getNPCAtTile(tileX: number, tileY: number): NPCPlacement | null {
    const npc = getNPCAtPosition(this.currentLocation, tileX, tileY);
    return npc || null;
  }

  /**
   * Check if tile is blocked by an NPC
   */
  isBlocked(tileX: number, tileY: number): boolean {
    const npc = this.getNPCAtTile(tileX, tileY);
    return npc?.blockMovement ?? false;
  }

  /**
   * Trigger interaction with NPC at tile position
   * Called when player bumps into or presses A near NPC
   * Returns true if an NPC was found and interacted with
   */
  tryInteract(tileX: number, tileY: number, facing: Direction): boolean {
    // Check if there's an NPC in front of the player
    let checkX = tileX;
    let checkY = tileY;

    switch (facing) {
      case 'up':
        checkY -= 1;
        break;
      case 'down':
        checkY += 1;
        break;
      case 'left':
        checkX -= 1;
        break;
      case 'right':
        checkX += 1;
        break;
    }

    const npc = this.getNPCAtTile(checkX, checkY);
    if (npc) {
      this.startDialogue(npc.npcId);

      // Make NPC face the player
      const instance = this.npcs.get(npc.npcId);
      if (instance) {
        const oppositeDir = this.getOppositeDirection(facing);
        instance.sprite.setDirection(oppositeDir);
      }
      return true;
    }
    return false;
  }

  /**
   * Get opposite direction (for NPC to face player)
   */
  private getOppositeDirection(direction: Direction): Direction {
    const opposites: Record<Direction, Direction> = {
      up: 'down',
      down: 'up',
      left: 'right',
      right: 'left',
    };
    return opposites[direction];
  }

  /**
   * Start dialogue with an NPC
   */
  startDialogue(npcId: string): void {
    // Get dialogue based on current story flags
    const dialogue = getNPCDialogue(npcId, this.currentLocation, this.activeFlags);

    // Find NPC in database to get speaker name
    const npcData = NPC_DIALOGUES.find(n => n.npcId === npcId);
    const speakerName = npcData?.name || 'NPC';

    // Show dialogue
    this.dialogueBox.showText(dialogue, speakerName, () => {
      // Dialogue complete callback
      if (this.interactionCallback) {
        this.interactionCallback(npcId);
      }
    });

    console.log(`[NPCManager] ${speakerName}: ${dialogue}`);
  }

  /**
   * Set callback for when interaction is complete
   */
  onInteraction(callback: (npcId: string) => void): void {
    this.interactionCallback = callback;
  }

  // =========================================================================
  // Update & Rendering
  // =========================================================================

  update(deltaTime: number): void {
    // Update all NPC sprites
    this.npcs.forEach(npc => {
      npc.sprite.update(deltaTime);
    });

    // Update dialogue box
    this.dialogueBox.update(deltaTime);
  }

  /**
   * Handle input for dialogue
   */
  handleInput(input: GameInput): boolean {
    // If dialogue is active, let it handle input
    if (this.dialogueBox.isActive()) {
      return this.dialogueBox.handleInput(input);
    }
    return false;
  }

  /**
   * Render all NPCs in the current location
   * @param renderer - The renderer to use
   * @param cameraX - Camera X position in pixels
   * @param cameraY - Camera Y position in pixels
   * @param screenWidth - Screen width in pixels
   * @param screenHeight - Screen height in pixels
   */
  render(
    renderer: Renderer,
    cameraX: number,
    cameraY: number,
    screenWidth: number = 256,
    screenHeight: number = 240
  ): void {
    // Render NPCs
    this.npcs.forEach(npc => {
      // Calculate screen position
      const screenX = npc.x - cameraX;
      const screenY = npc.y - cameraY;

      // Only render if on screen (with some margin)
      if (
        screenX > -this.TILE_SIZE &&
        screenX < screenWidth &&
        screenY > -this.TILE_SIZE &&
        screenY < screenHeight
      ) {
        // Render NPC sprite with shadow
        npc.sprite.renderWithShadow(renderer, screenX - 8, screenY - 8);

        // Debug: Draw name above NPC if close to player
        if (process.env.NODE_ENV === 'development') {
          renderer.drawText(
            npc.placement.name,
            screenX,
            screenY - 16,
            {
              font: 'ff1',
              fontSize: 6,
              color: '#FFFF00',
              align: 'center',
              shadow: true,
            }
          );
        }
      }
    });

    // Render dialogue box on top of everything
    this.dialogueBox.render(renderer);
  }

  // =========================================================================
  // Getters
  // =========================================================================

  /**
   * Check if dialogue is currently active
   */
  isDialogueActive(): boolean {
    return this.dialogueBox.isActive();
  }

  /**
   * Get all NPCs in current location
   */
  getAllNPCs(): NPCInstance[] {
    return Array.from(this.npcs.values());
  }

  /**
   * Get NPC by ID
   */
  getNPCById(npcId: string): NPCInstance | undefined {
    return this.npcs.get(npcId);
  }

  /**
   * Get current location
   */
  getCurrentLocation(): Location {
    return this.currentLocation;
  }

  /**
   * Get dialogue box (for manual control if needed)
   */
  getDialogueBox(): DialogueBox {
    return this.dialogueBox;
  }
}
